/**
 * B090012 在庫照会(受払)(本社業務)
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import { B090012Find, B090012FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  STable,
  SText,
  STextField,
} from "components";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  sokoCd: string;
  sokoNmr: string;
  shohinCd: string;
  shohinNmr: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  sokoCd: string;
  sokoNmr: string;
  shohinCd: string;
  shohinNmr: string;
  torihikiYmdFrom: string;
  torihikiYmdTo: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  crtDate: string;
  shainNm: string;
  ukeharaiKbnNm: string;
  ukeiresu: number;
  haraidashisu: number;
  hikiatesu: number;
  denpyoNo: number;
  torihikiYmdView: string;
  aitesakisokoCd: string;
  aitesakisokoNmr: string;
  meisaibiko: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  sokoCd: "",
  sokoNmr: "",
  shohinCd: "",
  shohinNmr: "",
  torihikiYmdFrom: "",
  torihikiYmdTo: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "crtDate", title: "更新日時", width: "10rem" },
  { field: "shainNm", title: "更新者", width: "7rem" },
  { field: "ukeharaiKbnNm", title: "受払区分", width: "11rem" },
  {
    field: "ukeiresu",
    title: "受入数　",
    width: "3rem",
    align: "right",
    render: (data) => data.ukeiresu?.toLocaleString(),
  },
  {
    field: "haraidashisu",
    title: "払出数　",
    width: "3rem",
    align: "right",
    render: (data) => data.haraidashisu?.toLocaleString(),
  },
  {
    field: "hikiatesu",
    title: "引当数　",
    width: "3rem",
    align: "right",
    render: (data) => data.hikiatesu?.toLocaleString(),
  },
  { field: "denpyoNo", title: "伝票番号", width: "8rem" },
  { field: "torihikiYmdView", title: "取引日", width: "7rem" },
  { field: "aitesakisokoCd", title: "相手先倉庫ｺｰﾄﾞ", width: "6rem" },
  { field: "aitesakisokoNmr", title: "相手先倉庫名", width: "8rem" },
  { field: "meisaibiko", title: "明細備考", width: "9rem" },
];

const focusId = "B090012";

// B090012 在庫照会(受払)(本社業務)
export const B090012 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 初期表示イベント
  useEffect(() => {
    // 処理詳細１
    // 倉庫コードセット
    if (props.sokoCd) {
      methods.setValue("sokoCd", props.sokoCd);
    }
    // 倉庫名表示
    if (props.sokoNmr) {
      methods.setValue("sokoNmr", props.sokoNmr);
    }
    // 商品コードセット
    if (props.shohinCd) {
      methods.setValue("shohinCd", props.shohinCd);
    }
    // 商品名表示
    if (props.shohinNmr) {
      methods.setValue("shohinNmr", props.shohinNmr);
    }
    // 初回検索
    (tableRef.current as any).onQueryChange();
    // 初回フォーカス先
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順の設定
      let order = ["crtDate,asc"];

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B090012FindRequest = {
        sokoCd: fields.sokoCd!,
        shohinCd: fields.shohinCd!,
        torihikiYmdFrom: fields.torihikiYmdFrom,
        torihikiYmdTo: fields.torihikiYmdTo,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼び出し
      B090012Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="在庫照会(受払)"
        gridTemplateColumns="6rem 11rem 1rem 11rem 3rem "
      >
        <SLabel>倉庫名</SLabel>
        <STextField label="倉庫名" name="sokoNmr" disabled />
        <Box gridColumn="span 3" />

        <SLabel>商品名</SLabel>
        <STextField
          label="商品名"
          name="shohinNmr"
          gridColumn="span 3"
          disabled
        />
        <Box />

        <SLabel>取引日</SLabel>
        <SDateField
          label="取引日(開始)"
          name="torihikiYmdFrom"
          {...register(1)}
        />
        <SText>～</SText>
        <SDateField
          label="取引日(終了)"
          name="torihikiYmdTo"
          {...register(2)}
        />

        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          {...register(3)}
        />
      </SFrame>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          height={320}
          width={1250}
        />
      </SFrame>
    </FormProvider>
  );
};
